import { initializeApp, FirebaseApp } from 'firebase/app';
import { initializeAppCheck, AppCheck, ReCaptchaV3Provider, getToken } from 'firebase/app-check';

// All values come from environment variables — set them in .env (local) or
// in your hosting provider's environment settings (production).
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

let app: FirebaseApp | null = null;
let appCheck: AppCheck | null = null;

function isConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
}

export function initializeFirebase(): void {
  if (!isConfigured()) {
    console.warn(
      '[AppCheck] Firebase env vars not set (VITE_FIREBASE_API_KEY, VITE_FIREBASE_PROJECT_ID, VITE_RECAPTCHA_SITE_KEY). App Check disabled.'
    );
    return;
  }

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;
  if (!siteKey) {
    console.warn('[AppCheck] VITE_RECAPTCHA_SITE_KEY not set. App Check disabled.');
    return;
  }

  try {
    app = initializeApp(firebaseConfig);

    // In development you can set VITE_FIREBASE_APPCHECK_DEBUG_TOKEN=true to
    // use the debug provider; Firebase will print the token to the console.
    if (import.meta.env.DEV) {
      // @ts-ignore – global flag consumed by the Firebase SDK
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });

    console.info('[AppCheck] Firebase App Check initialized');
  } catch (err) {
    console.error('[AppCheck] Failed to initialize Firebase App Check:', err);
  }
}

/**
 * Returns the current App Check token, or null if App Check is not available.
 */
export async function getAppCheckToken(): Promise<string | null> {
  if (!appCheck) return null;
  try {
    const result = await getToken(appCheck, false);
    return result.token;
  } catch (err) {
    console.warn('[AppCheck] Failed to get token:', err);
    return null;
  }
}
